import { attachmentSchema } from "@/api/attachment";
import { LocaleDict, multilingualFieldSchema } from "@/i18n";
import { z } from "zod";
import { artworkCoreInfoSchema, multipleprintCoreInfoSchema } from "../types";

export const sectionContentSchema = z.object({
  id: z.string(),
  sectionId: z.string(),
  title: z.string(),
  content: z.string(),
  published: z.boolean(),
});
export type SectionContent = z.infer<typeof sectionContentSchema>;

export const sectionSchema = z.object({
  id: z.string(),
  thumbnail: attachmentSchema,
  content: multilingualFieldSchema(sectionContentSchema),
  isGlobalSelection: z.boolean().default(false),
});
export type Section = z.infer<typeof sectionSchema>;

export interface SectionContentDto {
  title: string;
  content: string;
  published: boolean;
}
export interface CreateSectionDto {
  content: LocaleDict<SectionContentDto>;
  thumbnailId: string;
}

export interface EditSectionDto {
  id: string;
  content: LocaleDict<SectionContentDto>;
  thumbnailId: string;
}

export type LayoutAlignment = "CENTER" | "START" | "SPACE_AROUND";
export const LayoutAlignmentOptions: LayoutAlignment[] = [
  "CENTER",
  "START",
  "SPACE_AROUND",
];

export type LayoutCaptionAlignment = "LEFT" | "CENTER" | "RIGHT";
export const LayoutCaptionAlignmentOptions: LayoutCaptionAlignment[] = [
  "LEFT",
  "CENTER",
  "RIGHT",
];

export type LayoutNumberOfColumns = 2 | 3 | 4;
export const LayoutNumberOfColumnsOptions: number[] = [2, 3, 4];

export const layoutSchema = z.object({
  numberOfColumns: z.union([z.literal(2), z.literal(3), z.literal(4)]),
  alignment: z.enum(["CENTER", "START", "SPACE_AROUND"]),
  captionAlignment: z.enum(["LEFT", "CENTER", "RIGHT"]),
});

export type Layout = z.infer<typeof layoutSchema>;

const captionAlignmentSchema = z.enum(["LEFT", "CENTER", "RIGHT"]);

export type CaptionAlignment = z.infer<typeof captionAlignmentSchema>;

export const layoutNonNullableSchema = z.object({
  numberOfColumns: z.union([z.literal(2), z.literal(3), z.literal(4)]),
  alignment: z.enum(["CENTER", "START", "SPACE_AROUND"]),
  captionAlignment: captionAlignmentSchema,
});

export const captionAlignmentToTextAlignment = (alignment: CaptionAlignment) =>
  alignment === "LEFT" ? "left" : alignment === "CENTER" ? "center" : "right";

export type LayoutNonNullable = z.infer<typeof layoutNonNullableSchema>;

export const sectionArtworkCaptionSchema = sectionContentSchema.pick({
  content: true,
});

export type SectionArtworkCaption = z.infer<typeof sectionArtworkCaptionSchema>;

const sectionImageSchema = z.object({
  id: z.string(),
  imageType: z.literal("image"),
  url: z.string(),
  urlLarge: z.string().optional(),
  // Media kind: "image" (default) | "video" | "embed" — parité frontpublic
  kind: z.string().optional(),
  videoPublicUrl: z.string().optional().nullable(),
  posterPublicUrl: z.string().optional().nullable(),
  embedProvider: z.string().optional(),
  embedVideoId: z.string().optional(),
  embedUrl: z.string().optional(),
  videoLoop: z.boolean().optional(),
});

export const selectionSchema = z.object({
  position: z.number(),
  caption: multilingualFieldSchema(sectionArtworkCaptionSchema),
  item: z.discriminatedUnion("imageType", [
    sectionImageSchema,
    artworkCoreInfoSchema,
    multipleprintCoreInfoSchema,
  ]),
});

export type Selection = z.infer<typeof selectionSchema>;

export const sectionDescriptionVisibilitySchema = z.object({
  visibleTitles: z.boolean(),
  visibleAuthors: z.boolean(),
  visibleDates: z.boolean(),
  visibleTechniqueAndSupport: z.boolean(),
  visibleDimensions: z.boolean(),
  visibleBackText: z.boolean(),
  visibleSignature: z.boolean(),
  visibleCurrentCollection: z.boolean(),
  visibleRegisterNumber: z.boolean(),
  visibleCustomField: z.boolean(),
});

export type SectionDescriptionVisibility = z.infer<
  typeof sectionDescriptionVisibilitySchema
>;

export const sectionMetadataSchema = sectionDescriptionVisibilitySchema.extend({
  layout: layoutSchema,
});

export type SectionMetadata = z.infer<typeof sectionMetadataSchema>;

export const sectionImageCaptionSchema = multilingualFieldSchema(
  z.object({ content: z.string() }),
);

export type SectionImageCaption = z.infer<typeof sectionImageCaptionSchema>;

export type ImportImageDto = {
  imageId: string;
  caption: SectionImageCaption;
};
